/**
 * Broker dashboard route components. Each wraps a page in BrokerShell with
 * the appropriate title, nav ID, and fallback skeleton.
 */
import React, { lazy, useState, useCallback } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { BrokerShell } from '@layouts/BrokerShell';
import { PageErrorBoundary } from '@components/PageErrorBoundary';
import { PageSkeleton } from '@components/PageSkeleton';
import { TablePageSkeleton } from '@components/skeletons';
import { DealBySlugGate } from '@components/broker/DealBySlugGate';
import { useAuth } from '@contexts/AuthContext';
import { storeReturnAfterLogin } from '@utils/returnAfterLogin';

const MainBrokeragePage = lazy(() =>
  import('@pages/MainBrokeragePage').then((m) => ({ default: m.MainBrokeragePage }))
);
const SubBrokeragePage = lazy(() =>
  import('@pages/SubBrokeragePage').then((m) => ({ default: m.SubBrokeragePage }))
);
const WalletPage = lazy(() => import('@pages/WalletPage').then((m) => ({ default: m.WalletPage })));
const SettingsPage = lazy(() => import('@pages/SettingsPage').then((m) => ({ default: m.SettingsPage })));
const NewBrokerageRequestPage = lazy(() =>
  import('@pages/NewBrokerageRequestPage').then((m) => ({ default: m.NewBrokerageRequestPage }))
);
const LottieDevPage = lazy(() => import('@pages/LottieDevPage').then((m) => ({ default: m.LottieDevPage })));
const AccessDeniedBrokerPage = lazy(() =>
  import('@pages/broker/AccessDeniedBrokerPage').then((m) => ({ default: m.AccessDeniedBrokerPage }))
);
const TransactionDetailsPage = lazy(() =>
  import('@pages/TransactionDetailsPage').then((m) => ({ default: m.TransactionDetailsPage }))
);
const SubBrokerageTransactionDetailsPage = lazy(() =>
  import('@pages/SubBrokerageTransactionDetailsPage').then((m) => ({
    default: m.SubBrokerageTransactionDetailsPage,
  }))
);

function useLang() {
  const { lang } = useParams<{ lang: string }>();
  return lang === 'ar' ? 'ar' : 'en';
}

export function MainBrokerageRoute() {
  const l = useLang();
  const navigate = useNavigate();
  const { t } = useTranslation();
  return (
    <BrokerShell pageTitle={t('dashboard.mainBrokerage')} activeNavId="main" key="main-brokerage">
      <React.Suspense fallback={<TablePageSkeleton summaryCards={3} showSectionHeading tableColumns={8} />}>
        <MainBrokeragePage
          onViewDetails={(urlSlug) => { if (urlSlug) navigate(`/${l}/${urlSlug}`); }}
        />
      </React.Suspense>
    </BrokerShell>
  );
}

export function SubBrokerageRoute() {
  const l = useLang();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { clearSession } = useAuth();
  return (
    <BrokerShell pageTitle={t('dashboard.subBrokerage')} activeNavId="sub">
      <React.Suspense fallback={<TablePageSkeleton showSectionHeading tableColumns={9} showTablePager />}>
        <SubBrokeragePage
          onViewDetails={(urlSlug) => { if (urlSlug) navigate(`/${l}/${urlSlug}?view=sub`); }}
          onSignInRequired={() => {
            clearSession();
            if (typeof window !== 'undefined') {
              storeReturnAfterLogin(
                `${window.location.pathname}${window.location.search}${window.location.hash}`
              );
            }
            navigate(`/${l}/signup`, { replace: true });
          }}
        />
      </React.Suspense>
    </BrokerShell>
  );
}

export function WalletRoute() {
  const { t } = useTranslation();
  return (
    <BrokerShell pageTitle={t('dashboard.wallet')} activeNavId="wallet">
      <React.Suspense
        fallback={
          <TablePageSkeleton summaryCards={2} showContentCard showSectionHeading tableRows={4} tableColumns={6} tableWidth="md" />
        }
      >
        <WalletPage />
      </React.Suspense>
    </BrokerShell>
  );
}

export function BrokerSettingsRoute() {
  const { t } = useTranslation();
  return (
    <BrokerShell pageTitle={t('dashboard.settings')} activeNavId="settings">
      <React.Suspense fallback={<PageSkeleton />}>
        <SettingsPage />
      </React.Suspense>
    </BrokerShell>
  );
}

export function NewBrokerageRoute() {
  const l = useLang();
  const navigate = useNavigate();
  const { t } = useTranslation();
  return (
    <BrokerShell
      pageTitle={t('newBrokerage.pageTitle')}
      activeNavId="main"
      onBack={() => navigate(`/${l}/main-brokerage`)}
    >
      <React.Suspense fallback={<PageSkeleton />}>
        <NewBrokerageRequestPage onBackToBrokerage={() => navigate(`/${l}/main-brokerage`)} />
      </React.Suspense>
    </BrokerShell>
  );
}

export function LottieRoute() {
  const { t } = useTranslation();
  return (
    <BrokerShell pageTitle={t('devNav.lottie', { defaultValue: 'Lottie' })} activeNavId="main" key="lottie">
      <React.Suspense fallback={<PageSkeleton />}>
        <LottieDevPage />
      </React.Suspense>
    </BrokerShell>
  );
}

export function AccessDeniedRoute() {
  const l = useLang();
  const { t } = useTranslation();
  return (
    <BrokerShell
      pageTitle={t('brokerRouting.accessDeniedTitle', { defaultValue: 'Access denied' })}
      activeNavId="main"
    >
      <React.Suspense fallback={<PageSkeleton />}>
        <AccessDeniedBrokerPage lang={l} />
      </React.Suspense>
    </BrokerShell>
  );
}

/** Catch-all for deal slugs. Handles both main (?view default) and sub-brokerage (?view=sub) variants. */
export function DealSlugRoute() {
  const { lang, slug } = useParams<{ lang: string; slug: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();

  const l = lang === 'ar' ? 'ar' : 'en';
  const viewSub = searchParams.get('view') === 'sub';
  const variant = viewSub ? 'sub' : 'main';

  const [retryKey, setRetryKey] = useState(0);
  const [headerTitle, setHeaderTitle] = useState<string | null>(null);
  const handleHeaderTitle = useCallback((ref: string | null) => setHeaderTitle(ref), []);

  const dynamicTitle = headerTitle
    ? (() => {
        const ref = headerTitle;
        const key = viewSub ? 'subTransactionDetails.pageTitle' : 'transactionDetails.pageTitle';
        const raw = t(key, { reference: ref, id: ref });
        if (typeof raw === 'string' && raw.includes('{{')) {
          const base = t(
            viewSub ? 'subTransactionDetails.pageTitleLoading' : 'transactionDetails.pageTitleLoading',
            { defaultValue: 'Transaction details' }
          );
          return `${base} — ${ref}`;
        }
        return raw;
      })()
    : null;

  const fallbackTitle = t(
    viewSub ? 'subTransactionDetails.pageTitleLoading' : 'transactionDetails.pageTitleLoading',
    { defaultValue: 'Transaction details' }
  );

  if (!slug) return null;

  return (
    <BrokerShell
      key={variant}
      pageTitle={dynamicTitle || fallbackTitle}
      activeNavId={viewSub ? 'sub' : 'main'}
      onBack={() => navigate(`/${l}/${viewSub ? 'sub-brokerage' : 'main-brokerage'}`)}
      contentKey={variant}
    >
      <PageErrorBoundary
        key={`${slug}-${retryKey}`}
        onRetry={() => setRetryKey((k) => k + 1)}
        message={t('transactionDetails.loadError', {
          defaultValue: 'An unexpected error occurred. You can try again or refresh the page.',
        })}
      >
        <DealBySlugGate lang={l} slug={slug} variant={variant}>
          {(detail) => (
            <React.Suspense fallback={<PageSkeleton />}>
              {viewSub ? (
                <SubBrokerageTransactionDetailsPage
                  key={`${slug}-${retryKey}`}
                  transactionId={detail.id ?? slug}
                  onHeaderReference={handleHeaderTitle}
                />
              ) : (
                <TransactionDetailsPage
                  key={`${slug}-${retryKey}`}
                  transactionId={detail.id ?? slug}
                  onHeaderReference={handleHeaderTitle}
                  onDeleted={() => navigate(`/${l}/main-brokerage`)}
                />
              )}
            </React.Suspense>
          )}
        </DealBySlugGate>
      </PageErrorBoundary>
    </BrokerShell>
  );
}
